import type { FunctionComponent, ReactNode } from 'react';

import Icon from './Icon';
import { ICON_ALIGN } from './icon-constants';
import type { IconProps } from './Icon';
import type { IconAlign } from './icon-constants';

const DEFAULT_MARGIN_AMOUNT = '0.35em'; // em is relative to the parent's font-size, so this should work for all sizes

const margin = {
  left: {
    marginLeft: DEFAULT_MARGIN_AMOUNT,
  },
  right: {
    marginRight: DEFAULT_MARGIN_AMOUNT,
  },
};

type Props = {
  /**
   * Icon to render
   */
  icon: string;
  /**
   * Which side of the text the Icon should be rendered on
   */
  alignIcon?: IconAlign;
  /**
   * a CSS width to use for the margin between the icon and the text. If not set, a default margin size is used that's proportional to the font size.
   */
  marginAmount?: string;
  /**
   * Button contents to be displayed
   */
  children: ReactNode;
} & IconProps;

/**
 * Renders an Icon and a ReactNode inline.
 * Supports icons on the right or the left of the node.
 *
 * @example
 * import { IconAndNode, icons } from '../generic/Icon';
 *
 * const MyComponent = () => (
 *  <IconAndNode
 *    icon={icons.infoCircle}
 *    alignIcon="left"
 *  >
 *    <div>
 *      Hurray!
 *    </div>
 *  </IconAndNode>
 * );
 *
 * export default MyComponent;
 */
const IconAndNode: FunctionComponent<Props> = ({
  children,
  icon,
  alignIcon = 'left',
  marginAmount,
  ...iconProps
}) => (
  <>
    {alignIcon === ICON_ALIGN.Right && (
      <span style={marginAmount ? { marginRight: marginAmount } : margin.right}>
        {children}
      </span>
    )}
    <Icon icon={icon} {...iconProps} />
    {alignIcon === ICON_ALIGN.Left && (
      <span style={marginAmount ? { marginLeft: marginAmount } : margin.left}>
        {children}
      </span>
    )}
  </>
);

export default IconAndNode;
